import Image from 'next/image';
import Link from 'next/link';
import Trans from 'next-translate/Trans';
import type { ReactNode } from 'react';

import clsxm from '@/lib/clsxm';
import { localeDirection } from '@/lib/textDirection';

import Seo from '@/components/Seo';

import BrandPanel from './BrandPanel';
import { focusRing, linkClass, ShieldGlyph, tone, useAuthT } from './kit';

export const COMPLIANCE = ['SOC 2', 'ISO 27001', 'GDPR', 'HIPAA'];
export const TRUST_CENTER_URL = 'https://easypeasyai.trust.site/';

/**
 * The frame of every auth page: the form column (logo, the form, a trust
 * line) and, from lg up, the showcase panel. Phones get the form alone, so
 * the Google button is on the first screen even at 360x640.
 *
 * Auth pages are marketing-shell pages (no app sidebar), so Arabic is set
 * right to left here, on the root, like the landing pages built for it.
 */
export default function AuthLayout({
  title,
  children,
  panel = true,
}: {
  /** The <title>, already translated. */
  title: string;
  children: ReactNode;
  /** The showcase beside the form (lg+). */
  panel?: boolean;
}) {
  const { t, lang } = useAuthT();

  return (
    <div
      dir={localeDirection(lang)}
      className={clsxm(
        'min-h-[100dvh] [&:lang(ko)]:break-keep',
        panel &&
          'lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.04fr)] xl:grid-cols-[minmax(0,1fr)_minmax(0,1.12fr)]',
        tone.page,
      )}
    >
      <Seo templateTitle={title} />
      <div className='flex min-h-[100dvh] flex-col px-5 sm:px-10 lg:px-12 xl:px-16'>
        <header className='flex h-16 flex-none items-center sm:h-20'>
          <Link
            href='/'
            className={clsxm('-mx-1 rounded-lg px-1 py-1', focusRing)}
          >
            <Image
              src='/images/marky.png'
              width={500}
              height={72}
              preload
              alt='Easy-Peasy.AI'
              className='h-7 w-auto sm:h-8'
            />
          </Link>
        </header>
        <main className='flex flex-1 flex-col pb-10 pt-4 sm:justify-center sm:pb-16 sm:pt-6'>
          <div className='mx-auto w-full max-w-[400px]'>{children}</div>
        </main>
        <footer
          className={clsxm(
            'flex flex-none flex-wrap items-center justify-center gap-x-5 gap-y-2 pb-7 text-[12px] leading-5',
            tone.quiet,
          )}
        >
          <span className='inline-flex items-center gap-1.5'>
            <ShieldGlyph />
            <span dir='ltr'>{COMPLIANCE.join(' · ')}</span>
          </span>
          <a
            href={TRUST_CENTER_URL}
            target='_blank'
            rel='noopener noreferrer'
            className={clsxm(
              'rounded font-medium underline-offset-2 hover:text-[#10101c] hover:underline dark:hover:text-white',
              focusRing,
            )}
          >
            {t('Trust Center')}
          </a>
        </footer>
      </div>
      {panel && <BrandPanel />}
    </div>
  );
}

/** "By continuing, you agree to the Terms of Service and Privacy Policy." */
export function TermsNote({ className }: { className?: string }) {
  const linkProps = {
    target: '_blank',
    rel: 'noopener noreferrer',
    className: clsxm(linkClass, 'font-medium'),
  };
  return (
    <p
      className={clsxm(
        'text-center text-[13px] leading-5',
        tone.quiet,
        className,
      )}
    >
      <Trans
        i18nKey='By continuing, you agree to the <0>Terms of Service</0> and <1>Privacy Policy</1>.'
        ns='login'
        defaultTrans='By continuing, you agree to the <0>Terms of Service</0> and <1>Privacy Policy</1>.'
        components={[
          <Link key='terms' href='/terms-of-use' {...linkProps} />,
          <Link key='privacy' href='/privacy-policy' {...linkProps} />,
        ]}
      />
    </p>
  );
}
